import React, { useState, useEffect } from "react";
import "../../styles/external/affidavitTemplate.css";
import { X } from "lucide-react";

export const AffidavitTemplate = ({ affidavit_templates, onSelected }) => {
  const [templates, setTemplates] = useState({
    identity: [],
    legal: [],
    general: [],
  });

  const categorizeTemplates = (templateList) => {
    // Create a map for easy lookup
    const templateMap = {};
    templateList.forEach((template) => {
      templateMap[template.title.trim().toUpperCase()] = template;
    });

    // Identity & Personal - specific order as per design
    const identityOrder = [
      "CHANGE OF NAME",
      "DECLARATION OF AGE",
      "PROOF OF DEATH",
      "AFFIDAVIT OF SPINSTERHOOD",
      "CORRECTION OF NAME ON BIRTH CERTIFICATE",
    ];

    // Legal & Corporate - specific order as per design
    const legalOrder = [
      "CONTRACTOR REGISTRATION",
      "AFFIDAVIT OF SPONSORSHIP",
      "AFFIDAVIT FOR COMPLIANCE",
      "AFFIDAVIT OF BIDDING",
      "LOSS OF DRIVER LICENSE",
      "AFFIDAVIT OF FACTS",
      "LOSS OF INTERNATIONAL PASSPORT",
      "DAMAGED PASSPORT",
      "LOSS OF SIM CARD", // This might not exist in API, placeholder
      "DECLARATION OF MARRIAGE",
      "CONFIRMATION OF BACHELORSHIP",
      "LOSS OF DRIVEL LICENSE", // Duplicate of driver license
      "DECLARATION OF AGE", // Also appears here
      "AFFIDAVIT OF MEANS",
      "CHANGE OF OWNERSHIP(VEHICLE)",
    ];

    // General & Misc - specific order as per design
    const generalOrder = [
      "GENERAL AFFIDAVIT",
      "FORM A",
      "GOOD CONDUCT (WORK)",
      "GOOD CONDUCT (SCHOOL)",
      "GENERAL AFFIDAVIT 1", // Listed as "General Affidavit (2 Pas)"
    ];

    // Helper function to create formatted template
    const formatTemplate = (template, icon) => ({
      id: template.id,
      title: template.title.trim(),
      icon: icon,
      note: template.note,
    });

    // Build arrays based on the specific order
    const identity = [];
    const legal = [];
    const general = [];

    // Process Identity & Personal
    identityOrder.forEach((title) => {
      const template = templateMap[title];
      if (template) {
        identity.push(
          formatTemplate(
            template,
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.5 18.75C3.09 18.75 2.75 18.41 2.75 18V7C2.75 2.59 4.09 1.25 8.5 1.25H15.5C19.91 1.25 21.25 2.59 21.25 7V17C21.25 17.16 21.25 17.31 21.24 17.47C21.21 17.88 20.84 18.2 20.44 18.17C20.03 18.14 19.71 17.78 19.74 17.37C19.75 17.25 19.75 17.12 19.75 17V7C19.75 3.43 19.08 2.75 15.5 2.75H8.5C4.92 2.75 4.25 3.43 4.25 7V18C4.25 18.41 3.91 18.75 3.5 18.75Z"
                fill="#292D32"
              />
              <path
                d="M17 22.75H7C4.66 22.75 2.75 20.84 2.75 18.5V17.85C2.75 15.86 4.37 14.25 6.35 14.25H20.5C20.91 14.25 21.25 14.59 21.25 15V18.5C21.25 20.84 19.34 22.75 17 22.75ZM6.35 15.75C5.19 15.75 4.25 16.69 4.25 17.85V18.5C4.25 20.02 5.48 21.25 7 21.25H17C18.52 21.25 19.75 20.02 19.75 18.5V15.75H6.35Z"
                fill="#292D32"
              />
              <path
                d="M16 7.75H8C7.59 7.75 7.25 7.41 7.25 7C7.25 6.59 7.59 6.25 8 6.25H16C16.41 6.25 16.75 6.59 16.75 7C16.75 7.41 16.41 7.75 16 7.75Z"
                fill="#292D32"
              />
              <path
                d="M13 11.25H8C7.59 11.25 7.25 10.91 7.25 10.5C7.25 10.09 7.59 9.75 8 9.75H13C13.41 9.75 13.75 10.09 13.75 10.5C13.75 10.91 13.41 11.25 13 11.25Z"
                fill="#292D32"
              />
            </svg>
          )
        );
      }
    });

    // Process Legal & Corporate
    legalOrder.forEach((title) => {
      const template = templateMap[title];
      if (template) {
        legal.push(
          formatTemplate(
            template,
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.5 18.75C3.09 18.75 2.75 18.41 2.75 18V7C2.75 2.59 4.09 1.25 8.5 1.25H15.5C19.91 1.25 21.25 2.59 21.25 7V17C21.25 17.16 21.25 17.31 21.24 17.47C21.21 17.88 20.84 18.2 20.44 18.17C20.03 18.14 19.71 17.78 19.74 17.37C19.75 17.25 19.75 17.12 19.75 17V7C19.75 3.43 19.08 2.75 15.5 2.75H8.5C4.92 2.75 4.25 3.43 4.25 7V18C4.25 18.41 3.91 18.75 3.5 18.75Z"
                fill="#292D32"
              />
              <path
                d="M17 22.75H7C4.66 22.75 2.75 20.84 2.75 18.5V17.85C2.75 15.86 4.37 14.25 6.35 14.25H20.5C20.91 14.25 21.25 14.59 21.25 15V18.5C21.25 20.84 19.34 22.75 17 22.75ZM6.35 15.75C5.19 15.75 4.25 16.69 4.25 17.85V18.5C4.25 20.02 5.48 21.25 7 21.25H17C18.52 21.25 19.75 20.02 19.75 18.5V15.75H6.35Z"
                fill="#292D32"
              />
              <path
                d="M16 7.75H8C7.59 7.75 7.25 7.41 7.25 7C7.25 6.59 7.59 6.25 8 6.25H16C16.41 6.25 16.75 6.59 16.75 7C16.75 7.41 16.41 7.75 16 7.75Z"
                fill="#292D32"
              />
              <path
                d="M13 11.25H8C7.59 11.25 7.25 10.91 7.25 10.5C7.25 10.09 7.59 9.75 8 9.75H13C13.41 9.75 13.75 10.09 13.75 10.5C13.75 10.91 13.41 11.25 13 11.25Z"
                fill="#292D32"
              />
            </svg>
          )
        );
      }
    });

    // Process General & Misc
    generalOrder.forEach((title) => {
      const template = templateMap[title];
      if (template) {
        general.push(
          formatTemplate(
            template,
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.5 18.75C3.09 18.75 2.75 18.41 2.75 18V7C2.75 2.59 4.09 1.25 8.5 1.25H15.5C19.91 1.25 21.25 2.59 21.25 7V17C21.25 17.16 21.25 17.31 21.24 17.47C21.21 17.88 20.84 18.2 20.44 18.17C20.03 18.14 19.71 17.78 19.74 17.37C19.75 17.25 19.75 17.12 19.75 17V7C19.75 3.43 19.08 2.75 15.5 2.75H8.5C4.92 2.75 4.25 3.43 4.25 7V18C4.25 18.41 3.91 18.75 3.5 18.75Z"
                fill="#292D32"
              />
              <path
                d="M17 22.75H7C4.66 22.75 2.75 20.84 2.75 18.5V17.85C2.75 15.86 4.37 14.25 6.35 14.25H20.5C20.91 14.25 21.25 14.59 21.25 15V18.5C21.25 20.84 19.34 22.75 17 22.75ZM6.35 15.75C5.19 15.75 4.25 16.69 4.25 17.85V18.5C4.25 20.02 5.48 21.25 7 21.25H17C18.52 21.25 19.75 20.02 19.75 18.5V15.75H6.35Z"
                fill="#292D32"
              />
              <path
                d="M16 7.75H8C7.59 7.75 7.25 7.41 7.25 7C7.25 6.59 7.59 6.25 8 6.25H16C16.41 6.25 16.75 6.59 16.75 7C16.75 7.41 16.41 7.75 16 7.75Z"
                fill="#292D32"
              />
              <path
                d="M13 11.25H8C7.59 11.25 7.25 10.91 7.25 10.5C7.25 10.09 7.59 9.75 8 9.75H13C13.41 9.75 13.75 10.09 13.75 10.5C13.75 10.91 13.41 11.25 13 11.25Z"
                fill="#292D32"
              />
            </svg>
          )
        );
      } else if (
        title === "GENERAL AFFIDAVIT 1" &&
        templateMap["GENERAL AFFIDAVIT 1"]
      ) {
        // Handle the "2 Pas" variant
        const template = templateMap["GENERAL AFFIDAVIT 1"];
        general.push({
          ...formatTemplate(
            template,
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.5 18.75C3.09 18.75 2.75 18.41 2.75 18V7C2.75 2.59 4.09 1.25 8.5 1.25H15.5C19.91 1.25 21.25 2.59 21.25 7V17C21.25 17.16 21.25 17.31 21.24 17.47C21.21 17.88 20.84 18.2 20.44 18.17C20.03 18.14 19.71 17.78 19.74 17.37C19.75 17.25 19.75 17.12 19.75 17V7C19.75 3.43 19.08 2.75 15.5 2.75H8.5C4.92 2.75 4.25 3.43 4.25 7V18C4.25 18.41 3.91 18.75 3.5 18.75Z"
                fill="#292D32"
              />
              <path
                d="M17 22.75H7C4.66 22.75 2.75 20.84 2.75 18.5V17.85C2.75 15.86 4.37 14.25 6.35 14.25H20.5C20.91 14.25 21.25 14.59 21.25 15V18.5C21.25 20.84 19.34 22.75 17 22.75ZM6.35 15.75C5.19 15.75 4.25 16.69 4.25 17.85V18.5C4.25 20.02 5.48 21.25 7 21.25H17C18.52 21.25 19.75 20.02 19.75 18.5V15.75H6.35Z"
                fill="#292D32"
              />
              <path
                d="M16 7.75H8C7.59 7.75 7.25 7.41 7.25 7C7.25 6.59 7.59 6.25 8 6.25H16C16.41 6.25 16.75 6.59 16.75 7C16.75 7.41 16.41 7.75 16 7.75Z"
                fill="#292D32"
              />
              <path
                d="M13 11.25H8C7.59 11.25 7.25 10.91 7.25 10.5C7.25 10.09 7.59 9.75 8 9.75H13C13.41 9.75 13.75 10.09 13.75 10.5C13.75 10.91 13.41 11.25 13 11.25Z"
                fill="#292D32"
              />
            </svg>
          ),
          title: "GENERAL AFFIDAVIT (2 Pas)",
        });
      }
    });

    // Add any remaining templates that weren't in the specific orders
    templateList.forEach((template) => {
      const titleUpper = template.title.trim().toUpperCase();
      const isAlreadyAdded = [...identity, ...legal, ...general].some(
        (t) => t.id === template.id
      );

      if (!isAlreadyAdded) {
        // Determine category based on keywords
        if (
          titleUpper.includes("GENERAL") ||
          titleUpper.includes("FORM") ||
          titleUpper.includes("GOOD CONDUCT")
        ) {
          general.push(
            formatTemplate(
              template,
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.5 18.75C3.09 18.75 2.75 18.41 2.75 18V7C2.75 2.59 4.09 1.25 8.5 1.25H15.5C19.91 1.25 21.25 2.59 21.25 7V17C21.25 17.16 21.25 17.31 21.24 17.47C21.21 17.88 20.84 18.2 20.44 18.17C20.03 18.14 19.71 17.78 19.74 17.37C19.75 17.25 19.75 17.12 19.75 17V7C19.75 3.43 19.08 2.75 15.5 2.75H8.5C4.92 2.75 4.25 3.43 4.25 7V18C4.25 18.41 3.91 18.75 3.5 18.75Z"
                  fill="#292D32"
                />
                <path
                  d="M17 22.75H7C4.66 22.75 2.75 20.84 2.75 18.5V17.85C2.75 15.86 4.37 14.25 6.35 14.25H20.5C20.91 14.25 21.25 14.59 21.25 15V18.5C21.25 20.84 19.34 22.75 17 22.75ZM6.35 15.75C5.19 15.75 4.25 16.69 4.25 17.85V18.5C4.25 20.02 5.48 21.25 7 21.25H17C18.52 21.25 19.75 20.02 19.75 18.5V15.75H6.35Z"
                  fill="#292D32"
                />
                <path
                  d="M16 7.75H8C7.59 7.75 7.25 7.41 7.25 7C7.25 6.59 7.59 6.25 8 6.25H16C16.41 6.25 16.75 6.59 16.75 7C16.75 7.41 16.41 7.75 16 7.75Z"
                  fill="#292D32"
                />
                <path
                  d="M13 11.25H8C7.59 11.25 7.25 10.91 7.25 10.5C7.25 10.09 7.59 9.75 8 9.75H13C13.41 9.75 13.75 10.09 13.75 10.5C13.75 10.91 13.41 11.25 13 11.25Z"
                  fill="#292D32"
                />
              </svg>
            )
          );
        } else if (
          titleUpper.includes("NAME") ||
          titleUpper.includes("AGE") ||
          titleUpper.includes("DEATH") ||
          titleUpper.includes("BIRTH") ||
          titleUpper.includes("MARRIAGE") ||
          titleUpper.includes("BACHELOR") ||
          titleUpper.includes("SPINSTER") ||
          titleUpper.includes("SIGNATURE")
        ) {
          identity.push(
            formatTemplate(
              template,
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 12.75C8.83 12.75 6.25 10.17 6.25 7C6.25 3.83 8.83 1.25 12 1.25C15.17 1.25 17.75 3.83 17.75 7C17.75 10.17 15.17 12.75 12 12.75ZM12 2.75C9.66 2.75 7.75 4.66 7.75 7C7.75 9.34 9.66 11.25 12 11.25C14.34 11.25 16.25 9.34 16.25 7C16.25 4.66 14.34 2.75 12 2.75Z"
                  fill="#282828"
                />
                <path
                  d="M20.5901 22.75C20.1801 22.75 19.8401 22.41 19.8401 22C19.8401 18.55 16.3202 15.75 12.0002 15.75C7.68015 15.75 4.16016 18.55 4.16016 22C4.16016 22.41 3.82016 22.75 3.41016 22.75C3.00016 22.75 2.66016 22.41 2.66016 22C2.66016 17.73 6.85015 14.25 12.0002 14.25C17.1502 14.25 21.3401 17.73 21.3401 22C21.3401 22.41 21.0001 22.75 20.5901 22.75Z"
                  fill="#282828"
                />
              </svg>
            )
          );
        } else {
          legal.push(
            formatTemplate(
              template,
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.5 18.75C3.09 18.75 2.75 18.41 2.75 18V7C2.75 2.59 4.09 1.25 8.5 1.25H15.5C19.91 1.25 21.25 2.59 21.25 7V17C21.25 17.16 21.25 17.31 21.24 17.47C21.21 17.88 20.84 18.2 20.44 18.17C20.03 18.14 19.71 17.78 19.74 17.37C19.75 17.25 19.75 17.12 19.75 17V7C19.75 3.43 19.08 2.75 15.5 2.75H8.5C4.92 2.75 4.25 3.43 4.25 7V18C4.25 18.41 3.91 18.75 3.5 18.75Z"
                  fill="#292D32"
                />
                <path
                  d="M17 22.75H7C4.66 22.75 2.75 20.84 2.75 18.5V17.85C2.75 15.86 4.37 14.25 6.35 14.25H20.5C20.91 14.25 21.25 14.59 21.25 15V18.5C21.25 20.84 19.34 22.75 17 22.75ZM6.35 15.75C5.19 15.75 4.25 16.69 4.25 17.85V18.5C4.25 20.02 5.48 21.25 7 21.25H17C18.52 21.25 19.75 20.02 19.75 18.5V15.75H6.35Z"
                  fill="#292D32"
                />
                <path
                  d="M16 7.75H8C7.59 7.75 7.25 7.41 7.25 7C7.25 6.59 7.59 6.25 8 6.25H16C16.41 6.25 16.75 6.59 16.75 7C16.75 7.41 16.41 7.75 16 7.75Z"
                  fill="#292D32"
                />
                <path
                  d="M13 11.25H8C7.59 11.25 7.25 10.91 7.25 10.5C7.25 10.09 7.59 9.75 8 9.75H13C13.41 9.75 13.75 10.09 13.75 10.5C13.75 10.91 13.41 11.25 13 11.25Z"
                  fill="#292D32"
                />
              </svg>
            )
          );
        }
      }
    });

    const robj = { identity, legal, general };
    console.log("clean list");
    console.log(robj);
    return robj;
  };

  const handleSelectTemplate = (template) => {
    console.log("Selected template:", template);
    onSelected(template);
  };

  useEffect(() => {
    console.log("loaded template");
    console.log(affidavit_templates);
    setTemplates(categorizeTemplates(affidavit_templates));
  }, [affidavit_templates]);

  return (
    <div>
      {templates.identity.length > 0 && (
        <div className="section">
          <h3 className="section-title">Identity & Personal</h3>
          <div className="templates-grid">
            {templates.identity.map((template) => (
              <div
                key={template.id}
                className="template-card"
                onClick={() => handleSelectTemplate(template)}
              >
                <div className="template-info">
                  <div className="template-icon">{template.icon}</div>
                  <h4 className="template-title">{template.title}</h4>
                </div>
                <button className="select-btn">Select Template</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {templates.legal.length > 0 && (
        <div className="section">
          <h3 className="section-title">Legal & Corporate</h3>
          <div className="templates-grid">
            {templates.legal.map((template) => (
              <div
                key={template.id}
                className="template-card"
                onClick={() => handleSelectTemplate(template)}
              >
                <div className="template-info">
                  <div className="template-icon">{template.icon}</div>
                  <h4 className="template-title">{template.title}</h4>
                </div>
                <button className="select-btn">Select Template</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {templates.general.length > 0 && (
        <div className="section">
          <h3 className="section-title">General & Misc</h3>
          <div className="templates-grid">
            {templates.general.map((template) => (
              <div
                key={template.id}
                className="template-card"
                onClick={() => handleSelectTemplate(template)}
              >
                <div className="template-info">
                  <div className="template-icon">{template.icon}</div>
                  <h4 className="template-title">{template.title}</h4>
                </div>
                <button className="select-btn">Select Template</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
